import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { brl } from "@/lib/format";
import { TrendingUp, TrendingDown, ShoppingBag, Users, Package, DollarSign, Sparkles, Wallet } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { OnboardingChecklist, UpsellCard } from "@/components/Growth";
import { Link } from "react-router-dom";
import { PixCheckoutDialog } from "@/components/PixCheckout";
import { toast } from "sonner";
import { QrCode } from "lucide-react";

const PixRenewBanner = ({ sub, onRenewed }) => {
  const [pix, setPix] = useState(null);
  const { refresh } = useAuth();
  if (!sub || sub.billing_method !== "pix" || !sub.paid_until) return null;
  const days = Math.ceil((new Date(sub.paid_until) - Date.now()) / 86400000);
  if (days > 3) return null;
  const expired = days <= 0;
  const renew = async () => {
    try { const r = await api.post("/subscription/pix", { plan: sub.plan.id, period: "monthly" }); setPix(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar PIX"); }
  };
  return (
    <>
      <div className={`rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3 ${expired ? "bg-rose-50 border-rose-200 text-rose-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="pix-renew-banner">
        <div className="flex items-center gap-3">
          <QrCode className="h-6 w-6 shrink-0" />
          <div>
            <div className="font-bold">{expired ? "Sua mensalidade venceu" : `Sua mensalidade vence em ${days} dia${days === 1 ? "" : "s"}`}</div>
            <div className="text-sm opacity-80">Plano {sub.plan?.name} · válido até {new Date(sub.paid_until).toLocaleDateString("pt-BR")}. Renove via PIX e mantenha tudo ativo.</div>
          </div>
        </div>
        <Button onClick={renew} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="pix-renew-btn"><QrCode className="h-4 w-4" /> Renovar com PIX</Button>
      </div>
      <PixCheckoutDialog pix={pix} open={!!pix} onClose={() => setPix(null)} onPaid={() => { toast.success("Renovado com sucesso!"); refresh(); onRenewed(); }} />
    </>
  );
};

const KPI = ({ icon: Icon, label, value, delta, tone = "orange", testid }) => (
  <Card className="rounded-2xl border-stone-200 shadow-sm">
    <CardContent className="p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-xl grid place-items-center bg-${tone}-100 text-${tone}-600`}><Icon className="h-5 w-5" /></div>
        {delta != null && (
          <span className={`text-xs font-semibold ${delta >= 0 ? "text-emerald-600" : "text-rose-600"} inline-flex items-center gap-1`}>
            {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(delta).toFixed(0)}%
          </span>
        )}
      </div>
      <div className="mt-4 text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
      <div className="font-display text-2xl md:text-3xl font-extrabold text-stone-900 mt-1 font-mono-num">{value}</div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { restaurant } = useAuth();
  const [d, setD] = useState(null);
  const [sub, setSub] = useState(null);
  const loadSub = () => api.get("/subscription").then((r) => setSub(r.data)).catch(() => {});
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); loadSub(); }, []);
  const delta = d && d.revenue_yesterday ? ((d.revenue_today - d.revenue_yesterday) / d.revenue_yesterday) * 100 : 0;
  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PixRenewBanner sub={sub} onRenewed={loadSub} />
      {d?.alerts?.length > 0 && <div className="space-y-2" data-testid="dashboard-alerts">{d.alerts.map((a) => (
        <Link key={a.kind} to={a.to} className={`block rounded-xl border px-4 py-3 text-sm ${a.level === "error" ? "bg-rose-50 border-rose-200 text-rose-900" : a.level === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-sky-50 border-sky-200 text-sky-900"}`} data-testid={`alert-${a.kind}`}>
          <span className="font-semibold">{a.text}</span> <span className="opacity-70">— {a.why}</span></Link>))}</div>}
      <UpsellCard d={d} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm text-stone-500">Bem-vindo de volta</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-stone-900">{restaurant?.name}</h1>
        </div>
        {restaurant?.subscription_status === "trial" && (
          <Link to="/app/assinatura">
            <Button className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="trial-cta">
              <Sparkles className="h-4 w-4" /> Ativar assinatura
            </Button>
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={DollarSign} label="Faturamento hoje" value={brl(d?.revenue_today)} delta={delta} tone="orange" testid="kpi-revenue" />
        <KPI icon={ShoppingBag} label="Pedidos hoje" value={d?.orders_today ?? 0} tone="amber" testid="kpi-orders" />
        <KPI icon={TrendingUp} label="Ticket médio" value={brl(d?.ticket_avg)} tone="emerald" testid="kpi-ticket" />
        <KPI icon={Users} label="Clientes cadastrados" value={d?.customers ?? 0} tone="sky" testid="kpi-customers" />
      </div>
      <OnboardingChecklist />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Package} label="Produtos vendidos hoje" value={d?.items_sold_today ?? 0} tone="stone" testid="kpi-items" />
        <KPI icon={Wallet} label="Saldo financeiro (mês)" value={brl(d?.finance?.balance)} tone={d?.finance?.balance >= 0 ? "emerald" : "rose"} testid="kpi-balance" />
        <KPI icon={TrendingUp} label="Faturamento 7 dias" value={brl(d?.revenue_week)} delta={d?.revenue_prev_week ? ((d.revenue_week - d.revenue_prev_week) / d.revenue_prev_week) * 100 : null} tone="orange" testid="kpi-week" />
        <KPI icon={Users} label="Novos / recorrentes (mês)" value={`${d?.new_customers ?? 0} / ${d?.recurring_customers ?? 0}`} tone="sky" testid="kpi-newcust" />
      </div>
      <Card className="rounded-2xl border-stone-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-stone-900">Vendas — últimos 7 dias</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d?.chart_7d || []}>
                <CartesianGrid stroke="#f5f5f4" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#78716c" fontSize={12} />
                <YAxis stroke="#78716c" fontSize={12} tickFormatter={(v) => `R$ ${v}`} />
                <Tooltip formatter={(v) => brl(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#D94814" strokeWidth={3} dot={{ r: 4, fill: "#D94814" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="rounded-2xl"><CardContent className="p-6" data-testid="kpi-products">
          <Package className="h-6 w-6 text-orange-600 mb-2" />
          <div className="text-2xl font-mono-num font-bold">{d?.products ?? 0}</div>
          <div className="text-sm text-stone-500">Produtos ativos</div>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="p-6">
          <TrendingDown className="h-6 w-6 text-rose-600 mb-2" />
          <div className="text-2xl font-mono-num font-bold text-rose-600">{d?.low_stock ?? 0}</div>
          <div className="text-sm text-stone-500">Itens com estoque baixo</div>
        </CardContent></Card>
        <Card className="rounded-2xl"><CardContent className="p-6">
          <ShoppingBag className="h-6 w-6 text-emerald-600 mb-2" />
          <div className="text-2xl font-mono-num font-bold">{d?.orders_yesterday ?? 0}</div>
          <div className="text-sm text-stone-500">Pedidos ontem</div>
        </CardContent></Card>
      </div>
    </div>
  );
}
